import React, { useState, useEffect } from "react";
import {
  Paper,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useDispatch, useSelector } from "react-redux";
import { 
  GetTeamMembers, 
  DeletetUser, 
  GetPermissionGroups, 
  postUser, 
  postUserPermissions,
  GetUsers,
  GetUserInfo
} from "../../Redux/Actions/userStatusService";

function TeamManagement() {
  const dispatch = useDispatch();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [selectedGroupPermissions, setSelectedGroupPermissions] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [entityInfo, setEntityInfo] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch user info to get entity information
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await dispatch(GetUserInfo());
        if (response.payload?.statusCode === 200) {
          const userData = response.payload.data;
          setEntityInfo(userData?.entityInfo);
        } else {
          console.error("Failed to fetch user info for team management:", response);
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    fetchUserInfo();
  }, [dispatch]);

  // Fetch team members on component mount
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        const response = await dispatch(GetTeamMembers());
        
        if (response.payload?.statusCode === 200) {
          const membersData = response.payload.data || [];
          // Transform API data to match our component structure
          const transformedMembers = membersData.map(member => ({
            id: member.id,
            email: member.childUserRole?.user?.email || 'Unknown Email',
            status: member.status, // Use actual status from API
            name: member.childUserRole?.user?.userInfo?.firstName && member.childUserRole?.user?.userInfo?.lastName 
              ? `${member.childUserRole.user.userInfo.firstName} ${member.childUserRole.user.userInfo.lastName}`
              : member.childUserRole?.user?.userInfo?.firstName || member.childUserRole?.user?.userInfo?.lastName || 'Unknown Name',
            role: member.childUserRole?.role || 'SUB_MANAGER',
            createdAt: member.createdAt,
            allowedPermissions: member.allowedPermissions || [],
            // Get permission group names
            permissionGroups: member.allowedPermissions?.map(permission => 
              permission.permissionGroup?.groupName || 'Unknown Group'
            ) || [],
          }));
          setTeamMembers(transformedMembers);
        } else {
          console.error("Failed to fetch team members:", response);
          setSnackbar({
            open: true,
            message: "Failed to load team members",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
        setSnackbar({
          open: true,
          message: "Error loading team members",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, [dispatch]);

  // Fetch permission groups when dialog opens
  useEffect(() => {
    if (openDialog) {
      const fetchPermissionGroups = async () => {
        try {
          setLoadingGroups(true);
          const response = await dispatch(GetPermissionGroups());
          
          if (response.payload?.statusCode === 200) {
            const groupsData = response.payload.data || [];
            setPermissionGroups(groupsData);
          } else {
            console.error("Failed to fetch permission groups:", response);
          }
        } catch (error) {
          console.error("Error fetching permission groups:", error);
        } finally {
          setLoadingGroups(false);
        }
      };

      fetchPermissionGroups();
    }
  }, [openDialog, dispatch]);

  // Handle role selection change
  const handleRoleChange = (event) => {
    const selectedGroupName = event.target.value;
    setSelectedRole(selectedGroupName);
    
    // Find the selected group and get its permissions
    const selectedGroup = permissionGroups.find(group => group.groupName === selectedGroupName);
    if (selectedGroup) {
      setSelectedGroupPermissions(selectedGroup.apiNames || []);
    } else {
      setSelectedGroupPermissions([]);
    }
  };

  // Email validation
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setNewMemberEmail(value);
    if (!value) {
      setEmailError("Email is required");
    } else if (!validateEmail(value)) {
      setEmailError("Invalid email format");
    } else {
      setEmailError("");
    }
  };

  const handleAddMember = async () => {
    if (!newMemberEmail) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(newMemberEmail)) {
      setEmailError("Invalid email format");
      return;
    }
    if (!selectedRole) {
      setSnackbar({
        open: true,
        message: "Please select a role",
        severity: "error",
      });
      return;
    }

    try {
      setSubmitting(true);

      // Use entityInfo from GetUserInfo API
      const payload = {
        email: newMemberEmail,
        role: 'SUB_MANAGER', // Use the selected role from dropdown
        entityInfo: {
          name: entityInfo?.name || "",
          dateOfFormation: entityInfo?.dateOfFormation || "",
          entityType: entityInfo?.entityType || "MANAGER",
        },
      };


      const response = await dispatch(postUser(payload));

      if (response?.payload?.statusCode === 201) {
        // Get relationId from the response
        const relationId = response?.payload?.data?.relationId;

        if (relationId) {
          // Find the selected role to get its ID
          const selectedRoleObj = permissionGroups.find(role => role.groupName === selectedRole);
          const permissionGroupId = selectedRoleObj?.id;

          if (permissionGroupId) {
            const permissionsArray = [
              { permissionGroupId }
            ];

            const addUserPermissionBody = [
              {
                relationId,
                permissions: permissionsArray
              }
            ];


            const addUserPermissionResponse = await dispatch(postUserPermissions({ body: addUserPermissionBody }));

            if (addUserPermissionResponse?.payload?.statusCode === 201) {
              setSnackbar({
                open: true,
                message: "Invitation sent successfully!",
                severity: "success",
              });

              // Refresh team members list
              const refreshResponse = await dispatch(GetTeamMembers());
              if (refreshResponse.payload?.statusCode === 200) {
                const membersData = refreshResponse.payload.data || [];
                const transformedMembers = membersData.map(member => ({
                  id: member.id,
                  email: member.childUserRole?.user?.email || 'Unknown Email',
                  status: member.status,
                  name: member.childUserRole?.user?.userInfo?.firstName && member.childUserRole?.user?.userInfo?.lastName 
                    ? `${member.childUserRole.user.userInfo.firstName} ${member.childUserRole.user.userInfo.lastName}`
                    : member.childUserRole?.user?.userInfo?.firstName || member.childUserRole?.user?.userInfo?.lastName || 'Unknown Name',
                  role: member.childUserRole?.role || 'SUB_MANAGER',
                  createdAt: member.createdAt,
                  allowedPermissions: member.allowedPermissions || [],
                  permissionGroups: member.allowedPermissions?.map(permission => 
                    permission.permissionGroup?.groupName || 'Unknown Group'
                  ) || [],
                }));
                setTeamMembers(transformedMembers);
    
              }

              // Reset form
              setNewMemberEmail("");
              setSelectedRole("");
              setSelectedGroupPermissions([]);
              setEmailError("");
              setOpenDialog(false);
            } else {
              console.error("Failed to add user permissions:", addUserPermissionResponse);
              setSnackbar({
                open: true,
                message: addUserPermissionResponse?.payload?.message || "Failed to assign permissions",
                severity: "error",
              });
            }
          } else {
            console.error("Permission group ID not found for role:", selectedRole);
            setSnackbar({
              open: true,
              message: "Failed to find permission group for selected role",
              severity: "error",
            });
          }
        } else {
          console.error("Relation ID not found in response:", response);
          setSnackbar({
            open: true,
            message: "Failed to get relation ID from response",
            severity: "error",
          });
        }
      } else {
        console.error("Failed to add user:", response);
        setSnackbar({
          open: true,
          message: response?.payload?.message || "Failed to send invitation",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error adding team member:", error);
      setSnackbar({
        open: true,
        message: "Error sending invitation. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
  
      
      const response = await dispatch(DeletetUser({ id: memberId }));

      
      if (response.payload?.statusCode === 200) {
        setSnackbar({
          open: true,
          message: "Team member removed successfully!",
          severity: "success",
        });
        
        // Refresh team members list to show updated data
        const refreshResponse = await dispatch(GetTeamMembers());
        if (refreshResponse.payload?.statusCode === 200) {
          const membersData = refreshResponse.payload.data || [];
          const transformedMembers = membersData.map(member => ({
            id: member.id,
            email: member.childUserRole?.user?.email || 'Unknown Email',
            status: member.status,
            name: member.childUserRole?.user?.userInfo?.firstName && member.childUserRole?.user?.userInfo?.lastName 
              ? `${member.childUserRole.user.userInfo.firstName} ${member.childUserRole.user.userInfo.lastName}`
              : member.childUserRole?.user?.userInfo?.firstName || member.childUserRole?.user?.userInfo?.lastName || 'Unknown Name',
            role: member.childUserRole?.role || 'SUB_MANAGER',
            createdAt: member.createdAt,
            allowedPermissions: member.allowedPermissions || [],
            permissionGroups: member.allowedPermissions?.map(permission => 
              permission.permissionGroup?.groupName || 'Unknown Group'
            ) || [],
          }));
          setTeamMembers(transformedMembers);
        } else {
          console.error("Failed to refresh team members after deletion:", refreshResponse);
        }
      } else {
        console.error("Delete API response error:", response);
        setSnackbar({
          open: true,
          message: response?.payload?.message || "Failed to remove team member",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error removing team member:", error);
      setSnackbar({
        open: true,
        message: "Failed to remove team member. Please try again.",
        severity: "error",
      });
    }
  };


  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewMemberEmail("");
    setSelectedRole("");
    setSelectedGroupPermissions([]);
    setEmailError("");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "#4caf50"; // Light green
      case "PENDING":
        return "#ff9800"; // Orange
      case "DENIED":
        return "#f44336"; // Red
      default:
        return "#757575";
    }
  };

  const getStatusBackground = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "#e8f5e8"; // Light green background
      case "PENDING":
        return "#fff3e0"; // Light orange background
      case "DENIED":
        return "#ffebee"; // Light red background
      default:
        return "#f5f5f5";
    }
  };

  const isFormValid = newMemberEmail && validateEmail(newMemberEmail) && selectedRole;

  if (loading) {
    return (
      <Paper
        elevation={1}
        sx={{
          p: 3,
          minHeight: "500px",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          width: "80%",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: 600,
            fontSize: "1.25rem",
            color: "#333",
          }}
        >
          Your team
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ mb: 3, fontSize: "0.875rem" }}
        >
          Add sub-managers to manage strategies better.
        </Typography>
        
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          p: 3,
          minHeight: "500px",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          width: "80%",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: 600,
            fontSize: "1.25rem",
            color: "#333",
          }}
        >
          Your team
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ mb: 3, fontSize: "0.875rem" }}
        >
          Add sub-managers to manage strategies better.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 3, width: "100%" }}>
          <Button
            variant="contained"
            onClick={() => setOpenDialog(true)}
            fullWidth
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              px: 3,
              py: 1.5,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Add Sub-Manager
          </Button>
        </Box>

        <List sx={{ 
          width: "100%",
          maxHeight: "400px",
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "4px",
            "&:hover": {
              background: "#a8a8a8",
            },
          },
        }}>
          {teamMembers.map((member) => (
            <ListItem
              key={member.id}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                mb: 1,
                backgroundColor: "#fafafa",
                py: 2,
                px: 2,
              }}
            >
              <ListItemText
                primary={
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ 
                        fontWeight: 500, 
                        color: "#333",
                        fontSize: "0.875rem"
                      }}
                    >
                      {member.email}
                    </Typography>
                    {member.permissionGroups && member.permissionGroups.length > 0 && (
                      <Typography
                        variant="body2"
                        sx={{ 
                          color: "#666",
                          fontSize: "0.75rem",
                          mt: 0.5
                        }}
                      >
                        {member.permissionGroups.join(", ")}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    backgroundColor: getStatusBackground(member.status),
                    color: getStatusColor(member.status),
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "12px",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    border: `1px solid ${getStatusColor(member.status)}20`,
                  }}
                >
                  {member.status}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveMember(member.id)}
                  sx={{
                    color: "#f44336",
                    "&:hover": {
                      backgroundColor: "rgba(244, 67, 54, 0.08)",
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </ListItem>
          ))}
        </List>

        {teamMembers.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
            }}
          >
            <Typography variant="body1">
              No team members yet. Add your first sub-manager!
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Add Member Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: "#333" }}>
          Add Sub-Manager
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the email address and select a role for the person you want to invite as a sub-manager.
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newMemberEmail}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
                "&:hover fieldset": {
                  borderColor: "#1976d2",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976d2",
                },
              },
            }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role/Group</InputLabel>
            <Select
              value={selectedRole}
              label="Role/Group"
              onChange={handleRoleChange}
              disabled={loadingGroups}
            >
              {permissionGroups.map((group) => (
                <MenuItem key={group.id} value={group.groupName}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {group.groupName}
                    </Typography>
                    {group.description && (
                      <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                        {group.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedGroupPermissions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#333" }}>
                Permissions for selected role:
              </Typography>
              <Box sx={{ 
                maxHeight: "150px", 
                overflow: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                p: 1,
                backgroundColor: "#fafafa"
              }}>
                {selectedGroupPermissions.map((permission, index) => (
                  <Chip
                    key={index}
                    label={permission}
                    size="small"
                    sx={{ 
                      m: 0.5,
                      fontSize: "0.7rem",
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2"
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={submitting}
            sx={{
              color: "#666",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            disabled={!isFormValid || submitting}
            sx={{
              backgroundColor: "#1976d2",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#1565c0",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            {submitting ? "Sending..." : "Send Invitation"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TeamManagement; 